import { useEffect, useState } from 'react'
import { Pill } from 'lucide-react'
import Modal from '../../../../components/ERP/Modal/Modal'
import { patientService } from '../../../../services/patientApi'
import { INTERVALS, ROUTES, UNIT_OPTIONS, todayInputValue } from './medicationFormat'

const EMPTY = {
  name: '',
  dosageLabel: '',
  route: 'VO / Oral',
  scheduleKind: 'intervalo',
  firstDoseTime: '08:00',
  intervalHours: '8',
  doseTimes: '08:00, 12:00, 16:00',
  startDate: '',
  endDate: '',
  currentStock: '0',
  minStockAlert: '5',
  unitType: 'comprimido',
  doseAmount: '1',
  notes: '',
  prescribedBy: ''
}

function fromPrescription(item) {
  if (!item) return { ...EMPTY, startDate: todayInputValue() }
  return {
    name: item.name || '',
    dosageLabel: item.dosage_label || '',
    route: item.route || 'VO / Oral',
    scheduleKind: item.schedule_kind || 'intervalo',
    firstDoseTime: item.first_dose_time || '08:00',
    intervalHours: item.interval_hours ? String(item.interval_hours) : '8',
    doseTimes: (item.dose_times || []).join(', '),
    startDate: item.start_date || todayInputValue(),
    endDate: item.end_date || '',
    currentStock: item.current_stock == null ? '0' : String(item.current_stock),
    minStockAlert: item.min_stock_alert == null ? '' : String(item.min_stock_alert),
    unitType: item.unit_type || 'comprimido',
    doseAmount: item.dose_amount == null ? '1' : String(item.dose_amount),
    notes: item.notes || '',
    prescribedBy: item.prescribed_by ? String(item.prescribed_by) : ''
  }
}

function MedicationPrescriptionModal({ isOpen, patientId, prescription, allergies, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY)
  const [staff, setStaff] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!isOpen) return
    setForm(fromPrescription(prescription))
    setError('')
    patientService.staff().then((response) => setStaff(response.data || [])).catch(() => {})
  }, [isOpen, prescription])

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }))

  const payload = (confirmAllergy) => ({
    name: form.name,
    dosageLabel: form.dosageLabel,
    route: form.route,
    scheduleKind: form.scheduleKind,
    firstDoseTime: form.firstDoseTime,
    intervalHours: Number(form.intervalHours),
    doseTimes: form.doseTimes,
    startDate: form.startDate,
    endDate: form.endDate,
    currentStock: prescription ? undefined : Number(form.currentStock || 0),
    minStockAlert: form.minStockAlert,
    unitType: form.unitType,
    doseAmount: Number(form.doseAmount || 1),
    notes: form.notes,
    prescribedBy: form.prescribedBy || null,
    confirmAllergy
  })

  const submit = async (event, confirmAllergy = false) => {
    event?.preventDefault()
    const names = (allergies || []).map((item) => (typeof item === 'string' ? item : item.description)).filter(Boolean)
    const hit = names.some((allergy) => form.name.toLowerCase().includes(allergy.toLowerCase()) || allergy.toLowerCase().includes(form.name.toLowerCase()))
    if (hit && !confirmAllergy && !window.confirm(`O paciente tem alergia documentada a ${names.join(', ')}. Deseja prescrever mesmo assim?`)) return
    setSaving(true)
    setError('')
    try {
      if (prescription) await patientService.updatePrescription(patientId, prescription.id, payload(hit || confirmAllergy))
      else await patientService.createPrescription(patientId, payload(hit || confirmAllergy))
      onSaved()
      onClose()
    } catch (err) {
      const data = err.response?.data
      if (data?.code === 'ALLERGY_MATCH' && window.confirm(`${data.message}: ${(data.allergies || []).join(', ')}. Deseja prescrever mesmo assim?`)) {
        try {
          if (prescription) await patientService.updatePrescription(patientId, prescription.id, payload(true))
          else await patientService.createPrescription(patientId, payload(true))
          onSaved()
          onClose()
        } catch (retryError) {
          setError(retryError.response?.data?.message || 'Não foi possível salvar a prescrição')
        }
        return
      }
      setError(data?.message || 'Não foi possível salvar a prescrição')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={prescription ? 'Editar prescrição' : 'Nova prescrição'}
      subtitle="Horário, dose e estoque da casa"
      icon={<Pill size={22} />}
      size="medium"
      footer={(
        <button type="submit" form="prescription-form" disabled={saving}>
          {saving ? 'Salvando...' : 'Salvar prescrição'}
        </button>
      )}
    >
      <form id="prescription-form" onSubmit={submit}>
        {error && <p className="patients-alert-strip">{error}</p>}
        <div className="patients-form-grid patients-form-grid--2">
          <div className="patients-span-2">
            <label>Medicamento *</label>
            <input required value={form.name} onChange={set('name')} placeholder="Ex: Dipirona 500mg" />
          </div>
          <div>
            <label>Dosagem</label>
            <input value={form.dosageLabel} onChange={set('dosageLabel')} placeholder="Ex: 1 comprimido" />
          </div>
          <div>
            <label>Via</label>
            <select value={form.route} onChange={set('route')}>
              {ROUTES.map((item) => <option key={item}>{item}</option>)}
            </select>
          </div>
          <div>
            <label>Tipo de horário</label>
            <select value={form.scheduleKind} onChange={set('scheduleKind')}>
              <option value="intervalo">Intervalo</option>
              <option value="horarios">Horários do dia</option>
              <option value="se_necessario">Se necessário</option>
            </select>
          </div>
          {form.scheduleKind === 'intervalo' && (
            <>
              <div>
                <label>Primeira dose</label>
                <input type="time" value={form.firstDoseTime} onChange={set('firstDoseTime')} required />
              </div>
              <div>
                <label>Intervalo</label>
                <select value={form.intervalHours} onChange={set('intervalHours')}>
                  {INTERVALS.map((item) => <option key={item} value={item}>{item}/{item}h</option>)}
                </select>
              </div>
            </>
          )}
          {form.scheduleKind === 'horarios' && (
            <div className="patients-span-2">
              <label>Horários</label>
              <input value={form.doseTimes} onChange={set('doseTimes')} placeholder="08:00, 12:00, 16:00" required />
            </div>
          )}
          <div>
            <label>Início</label>
            <input type="date" value={form.startDate} onChange={set('startDate')} required />
          </div>
          <div>
            <label>Fim</label>
            <input type="date" value={form.endDate} onChange={set('endDate')} />
          </div>
          {!prescription && (
            <div>
              <label>Estoque atual</label>
              <input type="number" min="0" step="1" value={form.currentStock} onChange={set('currentStock')} />
            </div>
          )}
          <div>
            <label>Alertar a partir de</label>
            <input type="number" min="0" step="1" value={form.minStockAlert} onChange={set('minStockAlert')} placeholder="5" />
          </div>
          <div>
            <label>Unidade</label>
            <select value={form.unitType} onChange={set('unitType')}>
              {UNIT_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </div>
          <div>
            <label>Baixa por dose</label>
            <input type="number" min="0.01" step="any" value={form.doseAmount} onChange={set('doseAmount')} />
          </div>
          <div>
            <label>Prescrito por</label>
            <select value={form.prescribedBy} onChange={set('prescribedBy')}>
              <option value="">Usuário atual</option>
              {staff.map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}
            </select>
          </div>
          <div className="patients-span-2">
            <label>Observação</label>
            <textarea rows={2} value={form.notes} onChange={set('notes')} />
          </div>
        </div>
      </form>
    </Modal>
  )
}

export default MedicationPrescriptionModal
